import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  setDoc,
  deleteDoc
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { app } from "../firebase";
import { LuCheck, LuX, LuStore, LuPencil, LuCopy, LuPlus, LuBrush } from "react-icons/lu";
import LoadingComLayout from "../components/LoadingComLayout";

/* ======= CONSTANTES ======= */
// Force o bucket correto para evitar ambiguidade de ambiente
const STORAGE_BUCKET = "gs://bigode-app-fade.firebasestorage.app";

/* ======================= ESTILOS ======================= */
const Main = styled.main`
  flex: 1;
  padding: 2rem 4rem;
  background: #f3f7ff;
  min-height: 100vh;
  display: flex; flex-direction: column; gap: 1.25rem;

  @media (max-width: 1280px){ padding: 2rem; }
  @media (max-width: 768px){ padding: 1rem; }
`;

const PageWrapper = styled.div`
  display: grid;
  grid-template-columns: 560px 1fr;
  gap: 2rem;
  align-items: stretch;
  @media (max-width: 1200px) { grid-template-columns: 520px 1fr; }
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const Card = styled.section`
  background: #fff; border: 1px solid #e6ecf5; border-radius: 1rem;
  box-shadow: 0 6px 20px rgba(58,121,237,.06);
`;
const CardHeader = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:.6rem;
  padding: 1rem 1.1rem; border-bottom: 1px solid #eef2fb; color: #1f1a40; font-weight: 700;
  svg{ color:#3a79ed; }
`;
const CardBody = styled.div`
  padding: 1rem 1.1rem; display:flex; flex-direction:column; gap:.9rem;
`;

const IframePreview = styled.iframe`
  width: 100%;
  flex: 1;
  max-height: 800px;
  min-height: 800px;
  max-width: 400px;
  border: 10px solid #5c5c5c;
  border-radius: 4rem;
  background: white;
`;

const EditButton = styled.button`
  background: #3a79ed;
  color: white;
  border: none;
  border-radius: 0.7rem;
  padding: 0.55rem 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 700;
  box-shadow: 0 6px 18px rgba(58,121,237,.25);
  @media (max-width: 520px) {
    position: static;
    justify-content: center;
    margin-bottom: .75rem;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem; font-weight: 700; color: #1f1a40; margin: .25rem 0 1rem;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: .5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const StyledLink = styled.a`
  color: #3a79ed;
  font-size: 1rem;
  text-decoration: underline;
  display: inline-block;
  word-break: break-word;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ImagePreview = styled.label`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background-color: #e9e9e9;
  border: 2px dashed #868686;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  margin: 1rem 0 1.5rem;
  img { width: 100%; height: 100%; object-fit: cover; }
  svg { font-size: 1.6rem; color: #868686; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 0.75rem;
  row-gap: 1rem;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const GridArea = styled.div`
  display: grid; gap: 1rem; grid-template-columns: 2fr 1fr;
  @media (max-width: 1024px){ grid-template-columns: 1fr; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
  color: #1F1A40;
`;

const Input = styled.input`
  padding: 0.75rem;
  border-radius: 0.7rem;
  border: 1px solid #d1d5db;
  background: ${({ disabled }) => (disabled ? "#f3f4f6" : "#fff")};
  font-size: 0.95rem;
  color: #1F1A40;
  &::placeholder { color: #9ca3af; }
  &:focus {
    outline: none;
    border-color: #3a79ed;
    box-shadow: 0 0 0 3px rgba(58,121,237,.15);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  @media (max-width: 520px) { flex-direction: column; }
`;

// usar prop transitória ($danger) para não vazar para o DOM
const Button = styled.button`
  padding: 0.7rem 1.2rem;
  font-weight: 700;
  border: none;
  border-radius: 0.7rem;
  background-color: ${({ $danger }) => ($danger ? "#ed3a3a" : "#3a79ed")};
  color: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  &:hover { background-color: ${({ $danger }) => ($danger ? "#d43232" : "#2f66cb")}; }
`;

const ColorInput = styled.input.attrs({ type: "color" })`
  width: 60px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  appearance: none;
  background-color: transparent;
  box-shadow: 0 0 0 1px rgba(0,0,0,.06), 0 10px 36px rgba(0,0,0,.16);
  transition: transform .15s ease;
  &:hover { transform: scale(1.05); }
  &::-webkit-color-swatch-wrapper { padding: 0; border-radius: 8px; }
  &::-webkit-color-swatch { border: none; border-radius: 8px; }
  &::-moz-color-swatch { border: none; border-radius: 8px; }
`;

/* Carrossel */
const CarouselSection = styled.div` margin-top: 1.25rem; `;
const CarouselLabel = styled(Label)` margin-bottom: .5rem; `;
const CarouselStrip = styled.div`
  display: flex; gap: 1rem; margin-top: .5rem; padding-bottom: .5rem;
  overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: thin;
`;
const CarouselItem = styled.div` position: relative; scroll-snap-align: start; flex: 0 0 auto; `;
const CarouselImage = styled.img`
  width: 240px; height: 150px; object-fit: cover; border-radius: 0.7rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  @media (max-width: 520px) { width: 200px; height: 128px; }
`;
const RemoveBtn = styled.button`
  position: absolute; top: 6px; right: 6px; background: rgba(0,0,0,.6);
  border: none; border-radius: 50%; color: #fff; width: 24px; height: 24px;
  cursor: pointer; display: ${({ visible }) => (visible ? "flex" : "none")};
  align-items: center; justify-content: center;
`;
const AddTile = styled.label`
  width: 240px; height: 150px; border-radius: 0.7rem; border: 2px dashed #d1d5db;
  background: #f9fafb; display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex: 0 0 auto; scroll-snap-align: start; transition: background .15s ease;
  &:hover { background: #f3f4f6; }
  @media (max-width: 520px) { width: 200px; height: 128px; }
`;

const AtPrefix = styled.span`
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: #777; pointer-events: none; font-weight: 600;
`;


/* ======================= HELPERS ======================= */
function getExt(filename) {
  const raw = filename?.split(".").pop()?.toLowerCase();
  return raw && raw.length <= 5 ? raw : "jpg";
}

// === Helpers de telefone (BR) ===
function digitsOnly(s = "") {
  return s.replace(/\D/g, "");
}
function formatPhoneBR(value = "") {
  const v = digitsOnly(value).slice(0, 11);
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
}


/* ======================= COMPONENTE ======================= */
export default function MinhaBarbearia() {
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app, STORAGE_BUCKET);

  const [barbeariaId, setBarbeariaId] = useState(null);
  const [form, setForm] = useState(null);
  const [editando, setEditando] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [subdominio, setSubdominio] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [carrossel, setCarrossel] = useState([]);
  const [imagemSelecionada, setImagemSelecionada] = useState(null);
  const [imagensTemp, setImagensTemp] = useState([]);
  const carrosselRef = useRef(null);

  useEffect(() => { if (editando) scrollCarrosselToEnd(); }, [editando]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const q = query(collection(db, "barbearia"), where("criadorUid", "==", user.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const docSnap = snap.docs[0];
          const data = docSnap.data();
          const id = docSnap.id;

          setBarbeariaId(id);
          setForm({
            ...data,
            telefone: formatPhoneBR(data.telefone || ""),
            instagram: (data.instagram || "").replace(/@/g, "").replace(/\s+/g, "").toLowerCase(),
          });
          setSubdominio(id);
          setPreview(data.logo || "");

          // Carregar imagens do carrossel
          const imagensRef = collection(db, "barbearia", id, "imagensCarrossel");
          const imagensSnap = await getDocs(imagensRef);
          const imagens = imagensSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setCarrossel(imagens);
        }
      }
    });
    return () => unsub();
  }, []);

  const adicionarImagem = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // permite reenviar o mesmo arquivo depois
    if (!f || !barbeariaId) return;

    const tempId = `temp-${Date.now()}`;
    setImagensTemp(prev => [...prev, { id: tempId, preview: URL.createObjectURL(f) }]);
    setTimeout(scrollCarrosselToEnd, 50);

    try {
      // caminho correto para carrossel
      const ext = getExt(f.name);
      const path = `${barbeariaId}/carrossel/${Date.now()}_${f.name.replace(/\s+/g, "_")}`;
      const fileRef = ref(storage, path);

      await uploadBytes(fileRef, f, { contentType: f.type }); // passa nas regras isImage()
      const url = await getDownloadURL(fileRef);

      const novaRef = doc(collection(db, "barbearia", barbeariaId, "imagensCarrossel"));
      await setDoc(novaRef, { url, path });

      setImagensTemp(prev => prev.filter(img => img.id !== tempId));
      setCarrossel(prev => [...prev, { id: novaRef.id, url, path }]);
    } catch (eup) {
      console.error("Falha ao adicionar imagem do carrossel:", eup);
      setImagensTemp(prev => prev.filter(img => img.id !== tempId));
      // opcional: toast com erro
    }
  };

  const removerImagem = async (id) => {
    if (!barbeariaId) return;
    // apaga do Firestore e tenta remover do Storage (se tiver path salvo)
    const docRef = doc(db, "barbearia", barbeariaId, "imagensCarrossel", id);
    const item = carrossel.find(i => i.id === id);

    await deleteDoc(docRef);
    if (item?.path) {
      try { await deleteObject(ref(storage, item.path)); } catch { }
    }
    setCarrossel(prev => prev.filter(img => img.id !== id));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // máscara de telefone
    if (name === "telefone") {
      setForm(prev => ({ ...prev, telefone: formatPhoneBR(value) }));
      return;
    }

    // saneamento do instagram (sem @, sem espaço, minúsculo)
    if (name === "instagram") {
      const username = value.replace(/@/g, "").replace(/\s+/g, "").toLowerCase();
      setForm(prev => ({ ...prev, instagram: username }));
      return;
    }

    // suporte a campos aninhados (endereco.cidade, etc.)
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };


  const handleImage = (e) => {
    const imageFile = e.target.files?.[0];
    if (!imageFile) return;
    setFile(imageFile);
    setPreview(URL.createObjectURL(imageFile));
  };

  const salvar = async () => {
    if (!form || !barbeariaId) return;

    try {
      let logoUrl = form.logo || "";

      // Se trocou a logo, sobe para o Storage e pega a URL
      if (file) {
        const ext = getExt(file.name);
        const path = `${barbeariaId}/logo.${ext}`;
        const fileRef = ref(storage, path);

        // upload com contentType para passar nas regras
        await uploadBytes(fileRef, file, { contentType: file.type });

        // lê do MESMO ref
        logoUrl = await getDownloadURL(fileRef);
      }

      // Normalizações
      const phoneDigits = digitsOnly(form.telefone || "");
      const sanitizedInstagram = (form.instagram || "")
        .replace(/@/g, "")
        .replace(/\s+/g, "")
        .toLowerCase();

      // Payload final (preserva campos existentes + adiciona normalizados)
      const payload = {
        ...form,
        logo: logoUrl,
        telefone: form.telefone || "",   // formatado (exibição)
        telefoneRaw: phoneDigits,        // só dígitos (buscas/integr.)
        instagram: sanitizedInstagram,   // sem @, sem espaço, minúsculo
      };

      await updateDoc(doc(db, "barbearia", barbeariaId), payload);

      setEditando(false);
      setFile(null);
      setIframeKey((prev) => prev + 1);
      // opcional: toast de sucesso
    } catch (eup) {
      console.error("Erro ao salvar:", eup);
      // opcional: exibir mensagem amigável
    }
  };

  const cancelar = () => {
    setEditando(false);
    setFile(null);
    setPreview(form?.logo || "");
  };

  const copiarLink = () => {
    const link = `https://${subdominio}.bigode.app`;
    navigator.clipboard.writeText(link);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const scrollCarrosselToEnd = () => {
    if (carrosselRef.current) {
      carrosselRef.current.scrollTo({ left: carrosselRef.current.scrollWidth, behavior: "smooth" });
    }
  };

  if (!form) return <LoadingComLayout />;

  return (
    <>
      <Main>
        <GridArea>
          <Card>
            <CardHeader>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'.6rem'}}>
                <LuBrush />Detalhes da Barbearia
              </div>
              {!editando && (
                <EditButton onClick={() => setEditando(true)}>
                  <LuPencil color="white" /> Editar
                </EditButton>
              )}</CardHeader>
            <CardBody>

              {subdominio && (
                <LinkRow>
                  <StyledLink href={`https://${subdominio}.bigode.app`} target="_blank" rel="noreferrer">
                    {`https://${subdominio}.bigode.app`}
                  </StyledLink>
                  <IconButton onClick={copiarLink} title="Copiar link">
                    {copiado ? <LuCheck color="#3a79ed" size={20} /> : <LuCopy color="#3a79ed" size={20} />}
                  </IconButton>
                </LinkRow>
              )}
              <input
                type="file"
                id="logoInput"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImage}
              />
              <ImagePreview htmlFor="logoInput">
                {preview ? <img src={preview} alt="logo" /> : <LuStore />}
              </ImagePreview>
              <Grid>
                <FieldGroup>
                  <Label>Nome</Label>
                  <Input name="nome" value={form.nome || ""} onChange={handleInputChange} disabled={!editando} />
                </FieldGroup>
                <FieldGroup>
                  <Label>Telefone</Label>
                  <Input
                    name="telefone"
                    value={form.telefone || ""}
                    onChange={handleInputChange}
                    disabled={!editando}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(11) 91234-5678"
                    maxLength={16}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label>Instagram</Label>
                  <Input
                    name="instagram"
                    value={form.instagram || ""}
                    onChange={handleInputChange}
                    disabled={!editando}
                    autoComplete="username"
                    placeholder="suaBarbearia"
                    maxLength={30}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label>CEP</Label>
                  <Input name="endereco.cep" value={form.endereco?.cep || ""} onChange={handleInputChange} disabled={!editando} />
                </FieldGroup>
                <FieldGroup>
                  <Label>Endereço</Label>
                  <Input name="endereco.endereco" value={form.endereco?.endereco || ""} onChange={handleInputChange} disabled={!editando} />
                </FieldGroup>
                <FieldGroup>
                  <Label>Número</Label>
                  <Input name="endereco.numero" value={form.endereco?.numero || ""} onChange={handleInputChange} disabled={!editando} />
                </FieldGroup>
                <FieldGroup>
                  <Label>Cidade</Label>
                  <Input name="endereco.cidade" value={form.endereco?.cidade || ""} onChange={handleInputChange} disabled={!editando} />
                </FieldGroup>
                <FieldGroup>
                  <Label>Estado</Label>
                  <Input name="endereco.estado" value={form.endereco?.estado || ""} onChange={handleInputChange} disabled={!editando} />
                </FieldGroup>
              </Grid>
              <FieldGroup style={{ marginTop: "1.25rem" }}>
                <Label>Cor Primária</Label>
                <ColorInput
                  name="corPrimaria"
                  value={form.corPrimaria || "#3a79ed"}
                  onChange={handleInputChange}
                  disabled={!editando}
                />
              </FieldGroup>
              <CarouselSection>
                <CarouselLabel>Carrossel de Imagens</CarouselLabel>
                <CarouselStrip ref={carrosselRef}>
                  {!editando && carrossel.length === 0 && imagensTemp.length === 0 ? (
                    <p style={{ color: "#888", fontStyle: "italic", fontSize: 14 }}>
                      Nenhuma imagem cadastrada — colocamos imagens padrão na sua página de agendamento.
                    </p>
                  ) : (
                    [...carrossel, ...imagensTemp].map((img) => (
                      <CarouselItem key={img.id}>
                        <CarouselImage src={img.url || img.preview} alt="Carrossel" />
                        <RemoveBtn
                          visible={editando && !img.preview}
                          onClick={() => removerImagem(img.id)}
                          title="Remover imagem"
                        >
                          <LuX size={14} />
                        </RemoveBtn>
                      </CarouselItem>
                    ))
                  )}
                  {editando && (
                    <>
                      <AddTile htmlFor="novaImagem">
                        <LuPlus size={28} color="#888" />
                      </AddTile>
                      <input
                        id="novaImagem"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={adicionarImagem}
                      />
                    </>
                  )}
                </CarouselStrip>
              </CarouselSection>
              {editando && (
                <ButtonRow>
                  <Button onClick={salvar}><LuCheck /> Confirmar</Button>
                  <Button $danger onClick={cancelar}><LuX /> Cancelar</Button>
                </ButtonRow>
              )}
            </CardBody>
          </Card>
          {subdominio && (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ width: '100px', height: '30px', backgroundColor: '#5c5c5c', borderRadius: 20, marginBottom: '-50px', zIndex: 49, marginTop: 20 }}></div>
              <IframePreview
                key={iframeKey}
                src={`https://${subdominio}.bigode.app`}
                title="Visualização da página"
              />
            </div>
          )}
        </GridArea>
      </Main>
    </>
  );
}
